import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';
import { showErrorToast } from '@/common/utils/toast';

export interface Note {
  id: string;
  title: string;
  content: string;
  created_at: string;
  widget_id: string;
}

interface NotesState {
  notes: Note[];
  loading: boolean;
  fetchNotes: (widgetId: string) => Promise<void>;
  fetchNotesByIds: (ids: string[]) => Promise<Note[]>;
  addNote: (widgetId: string, title: string, content: string) => Promise<Note | null>;
  deleteNote: (id: string) => Promise<boolean>;
  updateNote: (widgetId: string, id: string, title: string, content: string) => Promise<boolean>;
}

export const useNotesStore = create<NotesState>((set) => ({
  notes: [],
  loading: false,

  fetchNotes: async (widgetId: string) => {
    set({ loading: true });
    await withAuthUser(async (userId) => {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', userId)
        .eq('widget_id', widgetId)
        .order('created_at', { ascending: false });

      if (!error) set({ notes: data });
      set({ loading: false });
      return data;
    });
  },

  fetchNotesByIds: async (ids: string[]) => {
    if (ids.length === 0) return [];
    const data = await withAuthUser(async (userId) => {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', userId)
        .in('id', ids);
      if (error) throw error;
      return data;
    });
    return data ?? [];
  },

  addNote: async (widgetId: string, title, content) => {
    const tempNote: Note = {
      id: 'temp-' + Date.now(),
      title,
      content,
      widget_id: widgetId,
      created_at: new Date().toISOString(),
    };

    set((state) => ({ notes: [tempNote, ...state.notes] }));

    try {
      const { data, error } = await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('notes')
          .insert([
            {
              title,
              content,
              user_id: userId,
              widget_id: widgetId,
            },
          ])
          .select();
        return { data, error };
      });

      if (error) throw error;

      if (data) {
        set((state) => ({
          notes: state.notes.map((note) => (note.id === tempNote.id ? data[0] : note)),
        }));
        return data[0];
      }
      return null;
    } catch (error) {
      set((state) => ({
        notes: state.notes.filter((note) => note.id !== tempNote.id),
      }));
      handleException(error);
      showErrorToast('Failed to create note', 'Your note could not be saved. Please try again.');
      return null;
    }
  },

  deleteNote: async (id: string) => {
    let deletedNote: Note | undefined;
    set((state) => {
      deletedNote = state.notes.find((note) => note.id === id);
      return { notes: state.notes.filter((note) => note.id !== id) };
    });

    try {
      const { error } = await withAuthUser(async (userId) => {
        const { error } = await supabase.from('notes').delete().match({ id }).eq('user_id', userId);
        return { error };
      });

      if (error) throw error;
      return true;
    } catch (error) {
      handleException(error);
      if (deletedNote) {
        set((state) => ({
          notes: [...state.notes, deletedNote!].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          ),
        }));
      }
      showErrorToast('Failed to delete note', 'The note could not be deleted. Please try again.');
      return false;
    }
  },

  updateNote: async (widgetId: string, id: string, title: string, content: string) => {
    let originalNote: Note | undefined;
    set((state) => {
      originalNote = state.notes.find((note) => note.id === id);
      return {
        notes: state.notes.map((note) => (note.id === id ? { ...note, title, content } : note)),
      };
    });

    try {
      const { error } = await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('notes')
          .update({ content, title })
          .match({ id })
          .eq('user_id', userId)
          .eq('widget_id', widgetId);
        return { error };
      });

      if (error) throw error;
      return true;
    } catch (error) {
      handleException(error);
      if (originalNote) {
        set((state) => ({
          notes: state.notes.map((note) => (note.id === id ? originalNote! : note)),
        }));
      }
      showErrorToast('Failed to save', 'Your changes could not be saved. Please try again.');
      return false;
    }
  },
}));
